import React, { useState } from 'react';
import axios from 'axios';
import { Switch } from './ui/switch';
import { Music, Megaphone, Star, UtensilsCrossed, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Feature toggles the venue admin can flip themselves.
// These persist on `user.features.*` so they survive session ends/restarts
// and are read by the public venue landing page to decide which tiles to show.
// `defaultOn` mirrors the backend `DEFAULT_USER_FEATURES` so the switch reads
// "on" for new accounts even before the user explicitly touches the toggle.
const FEATURE_TILES = [
  {
    key: 'song_requests',
    label: 'Song Requests',
    helper: 'Allow guests to request songs via the QR.',
    Icon: Music,
    accent: '#FF007F',
    defaultOn: true,
  },
  {
    key: 'shoutouts',
    label: 'Shoutouts',
    helper: 'Let guests send shoutout messages to the DJ booth.',
    Icon: Megaphone,
    accent: '#9D4CDD',
    defaultOn: true,
  },
  {
    key: 'reviews',
    label: 'Reviews',
    helper: 'Show the "Leave a Review" tile on the QR landing.',
    Icon: Star,
    accent: '#FFD700',
    defaultOn: true,
  },
  {
    key: 'menus',
    label: 'Menu',
    helper: 'Show the venue menu tile to guests scanning the QR.',
    Icon: UtensilsCrossed,
    accent: '#00FFFF',
    defaultOn: false,
  },
];

export const GuestExperienceCard = () => {
  const { user, refreshUser } = useAuth();
  const [saving, setSaving] = useState(null);
  // Optimistic override: { key, value } while a toggle is in flight, so the
  // switch reflects the user's choice immediately without waiting for the
  // network round trip + refreshUser to complete.
  const [optimistic, setOptimistic] = useState(null);

  const isOn = (key, defaultOn) => {
    if (optimistic && optimistic.key === key) return optimistic.value;
    const v = user?.features?.[key];
    return defaultOn ? v !== false : v === true;
  };

  const toggle = async (key, next) => {
    setSaving(key);
    setOptimistic({ key, value: next });
    try {
      await axios.put(`${API}/auth/me/features`, { [key]: next });
      await refreshUser();
      toast.success(next ? 'Tile shown to guests' : 'Tile hidden from guests');
    } catch (err) {
      console.warn(`[GuestExperience] toggle ${key} failed:`, err);
      toast.error('Could not update — try again');
    } finally {
      setSaving(null);
      setOptimistic(null);
    }
  };

  return (
    <div
      className="bg-white/[0.03] border border-white/10 rounded-2xl lg:rounded-3xl p-5 lg:p-6 backdrop-blur-xl"
      data-testid="guest-experience-card"
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#FF007F]" />
          <h3
            className="text-sm font-extrabold text-white uppercase tracking-[0.18em]"
            style={{ fontFamily: 'Outfit' }}
          >
            Guest Experience
          </h3>
        </div>
      </div>
      <p className="text-gray-500 text-[11px] mb-4">
        Pick which tiles appear when guests scan your QR. Turning a tile off hides it from every guest, even when no DJ session is live.
      </p>

      <div className="space-y-2.5">
        {FEATURE_TILES.map(({ key, label, helper, Icon, accent, defaultOn }) => {
          const on = isOn(key, defaultOn);
          return (
            <div
              key={key}
              className="flex items-center justify-between gap-3 p-3 rounded-xl bg-black/30 border border-white/5"
              data-testid={`guest-experience-row-${key}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${accent}20`, color: accent }}
                >
                  <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-white text-sm font-semibold leading-tight">{label}</p>
                  <p className="text-gray-500 text-[11px] leading-snug">{helper}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {saving === key && <Loader2 className="w-3.5 h-3.5 text-gray-500 animate-spin" />}
                <Switch
                  checked={on}
                  onCheckedChange={(v) => toggle(key, v)}
                  disabled={saving === key}
                  className="data-[state=checked]:bg-[#39FF14]"
                  data-testid={`guest-experience-toggle-${key}`}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
